import { ReactNode, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useScrollVisibility } from '@/hooks/useScrollVisibility';
import { usePopoverCloseSignal } from '@/stores';
import { useEventFeedStore } from '@/stores/event-feed.store';
import { cn } from '@/lib/utils';

type Alignment = 'left' | 'center';

interface AutoHideOnScrollProps {
    children: ReactNode;
    container: HTMLElement | null;
    enabled?: boolean;
    align?: Alignment;
    className?: string;
}

const alignmentClasses: Record<Alignment, string> = {
    left: 'left-4',
    center: 'left-1/2 -translate-x-1/2',
};

export function AutoHideOnScroll({
    children,
    container,
    enabled = true,
    align = 'center',
    className,
}: AutoHideOnScrollProps) {
    const { isVisible } = useScrollVisibility(container, {
        enabled,
    });
    const triggerPopoverClose = usePopoverCloseSignal((state) => state.trigger);
    const playerIsFullscreen = useEventFeedStore(
        (state) => state.playerIsFullscreen,
    );

    useEffect(() => {
        if (!isVisible) {
            triggerPopoverClose();
        }
    }, [isVisible, triggerPopoverClose]);

    return (
        <div
            className={cn(
                'absolute top-8 z-50',
                alignmentClasses[align],
                playerIsFullscreen && 'hidden',
                className,
            )}
        >
            <motion.div
                animate={
                    isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: -40 }
                }
                initial={{ opacity: 1, y: 0 }}
                transition={{
                    duration: 0.25,
                    ease: [0.4, 0, 0.2, 1],
                }}
                style={{ pointerEvents: isVisible ? 'auto' : 'none' }}
            >
                {children}
            </motion.div>
        </div>
    );
}
